import Input from "@/lib/components/ui/input/input";
import { cn } from "@/lib/utils";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type ChangeEvent,
  type ComponentProps,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { filterHexInput, isValidHex, normalizeHex } from "./color-field.utils";

type ColorFieldContextValue = {
  colors: readonly string[];
  disabled?: boolean;
  onValueChange: (value: string) => void;
  value: string;
};

const ColorFieldContext = createContext<ColorFieldContextValue | null>(null);

function useColorField(): ColorFieldContextValue {
  const ctx = useContext(ColorFieldContext);
  if (!ctx) {
    throw new Error("ColorField subcomponents must be used within ColorField");
  }
  return ctx;
}

export type ColorFieldProps = {
  children?: ReactNode;
  className?: string;
  colors: readonly string[];
  disabled?: boolean;
  onValueChange: (value: string) => void;
  value: string;
};

export function ColorField({
  children,
  className,
  colors,
  disabled,
  onValueChange,
  value,
}: ColorFieldProps) {
  const ctx = useMemo<ColorFieldContextValue>(
    () => ({
      colors,
      disabled,
      onValueChange,
      value,
    }),
    [colors, disabled, onValueChange, value],
  );

  return (
    <ColorFieldContext.Provider value={ctx}>
      <div className={cn("flex flex-col gap-3", className)}>{children}</div>
    </ColorFieldContext.Provider>
  );
}

export type ColorFieldSwatchesProps = {
  className?: string;
  getSwatchAriaLabel: (hex: string) => string;
  groupAriaLabel: string;
};

function colorFieldSwatchValueInList(
  colors: readonly string[],
  current: string,
): string {
  const upper = current.toUpperCase();
  const matched = colors.find((hex) => hex.toUpperCase() === upper);
  return matched ?? "";
}

export function ColorFieldSwatches({
  className,
  getSwatchAriaLabel,
  groupAriaLabel,
}: ColorFieldSwatchesProps) {
  const { colors, disabled, onValueChange, value } = useColorField();
  const groupValue = colorFieldSwatchValueInList(colors, value);

  return (
    <RadioGroupPrimitive.Root
      aria-label={groupAriaLabel}
      className={cn("flex flex-wrap gap-3 px-1", className)}
      disabled={disabled}
      onValueChange={onValueChange}
      value={groupValue}
    >
      {colors.map((hex) => (
        <RadioGroupPrimitive.Item key={hex} value={hex} asChild>
          <button
            aria-label={getSwatchAriaLabel(hex)}
            className={cn(
              "interactive flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full border-2",
              "border-border hover:border-foreground-secondary",
              "data-[state=checked]:ring-offset-background data-[state=checked]:ring-foreground data-[state=checked]:ring-2 data-[state=checked]:ring-offset-2",
            )}
            style={{ backgroundColor: hex }}
            type="button"
          >
            <span className="sr-only">{getSwatchAriaLabel(hex)}</span>
          </button>
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  );
}

export type ColorFieldPreviewProps = {
  className?: string;
  /** Announced for the native color control (click the swatch to open the picker). */
  pickerAriaLabel?: string;
};

export function ColorFieldPreview({
  className,
  pickerAriaLabel = "Choose color",
}: ColorFieldPreviewProps) {
  const { disabled, onValueChange, value } = useColorField();
  const bg = isValidHex(value) ? value : undefined;

  const pickerValue = isValidHex(value)
    ? (normalizeHex(value) ?? value).toLowerCase()
    : "#000000";

  function handlePickerChange(event: ChangeEvent<HTMLInputElement>) {
    const normalized = normalizeHex(event.target.value);
    if (normalized) {
      onValueChange(normalized);
    }
  }

  return (
    <div
      style={bg ? { backgroundColor: bg } : undefined}
      className={cn(
        "border-border relative h-10 w-14 shrink-0 overflow-hidden rounded-md border shadow-xs",
        !disabled && "cursor-pointer",
        className,
      )}
    >
      <input
        type="color"
        value={pickerValue}
        disabled={disabled}
        onChange={handlePickerChange}
        className={cn(
          "absolute inset-0 h-full w-full cursor-pointer border-0 bg-transparent p-0 opacity-0",
          disabled && "pointer-events-none cursor-not-allowed",
        )}
        aria-label={pickerAriaLabel}
      />
    </div>
  );
}

export type ColorFieldHexInputProps = Omit<
  ComponentProps<typeof Input>,
  "onBlur" | "onChange" | "size" | "type" | "value"
> & {
  onBlur?: () => void;
};

export function ColorFieldHexInput({
  onBlur,
  className,
  onKeyDown,
  ...props
}: ColorFieldHexInputProps) {
  const baseId = useId();
  const { disabled, onValueChange, value } = useColorField();

  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);

  useEffect(
    function syncDraftFromCommittedValue() {
      setDraft(value);
    },
    [value],
  );

  const unfocusedDisplay = isValidHex(value) ? normalizeHex(value)! : value;
  const display = focused ? draft : unfocusedDisplay;

  const commit = useCallback(
    function commitHexInput() {
      const normalized = normalizeHex(draft);
      if (normalized) {
        onValueChange(normalized);
        setDraft(normalized);
      } else {
        setDraft(value);
      }
    },
    [draft, onValueChange, value],
  );

  function handleBlur() {
    setFocused(false);
    commit();
    onBlur?.();
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    setDraft(filterHexInput(event.target.value));
  }

  function handleFocus() {
    setFocused(true);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      commit();
    }
    onKeyDown?.(event);
  }

  return (
    <Input
      {...props}
      className={cn("font-mono", className)}
      disabled={disabled}
      id={props.id ?? baseId}
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      size="md"
      spellCheck={false}
      type="text"
      value={display}
    />
  );
}
